import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";

/**
 * Interface for the props of the CustomToolTip component.
 */
interface CustomToolTipProps {
  /**
   * The element that will trigger the tooltip.
   */
  toolTipTrigger: React.ReactNode;
  /**
   * The content of the tooltip.
   */
  toolTipContent: React.ReactNode;

  handleClick?: () => void;
  className?: string;
}

/**
 * CustomToolTip Component
 * @param {CustomToolTipProps} props - Props for the CustomToolTip component.
 * @param {React.ReactNode} props.toolTipTrigger - The element that will trigger the tooltip.
 * @param {React.ReactNode} props.toolTipContent - The content of the tooltip.
 * @returns {React.ReactElement} - A tooltip component.
 * @example
 * <CustomToolTip
 *   toolTipTrigger={<span>Hover me</span>}
 *   toolTipContent={<span>Tooltip content</span>}
 * />
 * @description - This component is used to create a custom tooltip.
 */
export function CustomToolTip({
  toolTipContent,
  toolTipTrigger,
  handleClick,
  className,
}: CustomToolTipProps): React.ReactElement {
  return (
    <TooltipProvider delayDuration={100}>
      <Tooltip>
        <TooltipTrigger
          asChild
          className={cn("cursor-pointer", className)}
          onClick={handleClick}
        >
          {toolTipTrigger}
        </TooltipTrigger>
        <TooltipContent>{toolTipContent}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
